import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import CommonPopover from '@shared/components/ui/CommonPopover';
import { UI_PROFILE_PATH } from '@shared/constants/pathsConstants';
import UGLogo from '@shared/static/icons/UGLogo';
import { useTranslation } from 'react-i18next';
import { LS_SELECTED_LANGUAGE } from '@shared/constants/storagesKeysConstants';
import { logoutRequest } from '@features/auth/store';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import BugReportIcon from '@mui/icons-material/BugReport';
import { DOCS_LINK, JIRA_PROJECT_LINK, VIDEO_TUTORIAL_LINK } from '@shared/constants/urlsConstants';
import { useColorModeContext, ThemeMode } from '@app/providers/ColorModeProvider';
import { AppLanguage } from '@shared/types';
import { toggleSidebarCollapsed } from '@features/favorites/store';

const RootStyles = {
    zIndex: 2,
    position: 'fixed',
    width: '100vw',
    height: '48px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '10px',
    padding: '0 20px 0 16px',
};

const PopoverItemStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: 0.5,
    cursor: 'pointer',
    fontWeight: 600,
    padding: '5px 10px',
    borderRadius: '5px',
    color: 'inherit',
    textDecoration: 'inherit',
    '&:hover': {
        backgroundColor: 'var(--primary-light-color)',
        color: 'white',
    },
};

const PopoverItemThemeStyles = {
    gap: 1,
    alignItems: 'center',
};

const Topbar = () => {
    const theme = useTheme();
    const { themeMode, setThemeMode } = useColorModeContext();
    const [questionAnchorEl, setQuestionAnchorEl] = useState<HTMLElement | null>(null);
    const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(null);
    const [languageAnchorEl, setLanguageAnchorEl] = useState<HTMLElement | null>(null);
    const [themeAnchorEl, setThemeAnchorEl] = useState<HTMLElement | null>(null);
    const dispatch = useDispatch();
    const { i18n, t } = useTranslation();

    const handleCollapseSidebarButton = () => {
        dispatch(toggleSidebarCollapsed());
    };

    const handleLanguageChange = (newLang: AppLanguage) => async () => {
        await i18n.changeLanguage(newLang);
        localStorage.setItem(LS_SELECTED_LANGUAGE, newLang);
        setLanguageAnchorEl(null);
    };

    const handleThemeChange = (mode: ThemeMode) => () => {
        setThemeMode(mode);
        setThemeAnchorEl(null);
    };

    const handleLogout = () => {
        dispatch(logoutRequest());
    };

    const handleCloseQuestionMenu = () => {
        setQuestionAnchorEl(null);
    };

    const getThemeIcon = () => {
        switch (themeMode) {
            case ThemeMode.LIGHT:
                return <LightModeOutlinedIcon />;
            case ThemeMode.DARK:
                return <DarkModeOutlinedIcon />;
            case ThemeMode.SYSTEM:
                return <SettingsBrightnessIcon />;
            default:
                return <LightModeOutlinedIcon />;
        }
    };

    return (
        <>
            <Box
                data-testid="topbar"
                sx={{
                    backgroundColor: (theme.palette as any).components.navigation.background,
                    ...RootStyles,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={handleCollapseSidebarButton} data-testid="sidebar_collapse_btn">
                        <MenuIcon />
                    </IconButton>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            marginLeft: 4,
                            marginRight: 2,
                        }}
                        data-testid="logo"
                    >
                        <UGLogo />
                        <Typography sx={{ fontSize: '1.25rem', whiteSpace: 'nowrap' }}>
                            UserGate Test Management System
                        </Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifySelf: 'flex-end',
                        marginLeft: 'auto',
                        alignItems: 'center',
                    }}
                >
                    <CommonPopover anchorEl={questionAnchorEl} setAnchorEl={setQuestionAnchorEl}>
                        <Box
                            component="a"
                            href={DOCS_LINK}
                            target="_blank"
                            rel="noreferrer"
                            sx={PopoverItemStyles}
                            onClick={handleCloseQuestionMenu}
                            data-testid="docs_link"
                        >
                            <DescriptionOutlinedIcon sx={{ fontSize: 17 }} />
                            {t('documentation')}
                        </Box>
                        <Box
                            component="a"
                            href={VIDEO_TUTORIAL_LINK}
                            target="_blank"
                            rel="noreferrer"
                            sx={PopoverItemStyles}
                            onClick={handleCloseQuestionMenu}
                            data-testid="video_tutorial_link"
                        >
                            <OndemandVideoIcon sx={{ fontSize: 17 }} />
                            {t('video')}
                        </Box>
                        <Box
                            component="a"
                            href={JIRA_PROJECT_LINK}
                            target="_blank"
                            rel="noreferrer"
                            sx={PopoverItemStyles}
                            onClick={handleCloseQuestionMenu}
                            data-testid="jira_link"
                        >
                            <BugReportIcon sx={{ fontSize: 17 }} />
                            {t('make_issue')}
                        </Box>
                    </CommonPopover>
                    <CommonPopover anchorEl={profileAnchorEl} setAnchorEl={setProfileAnchorEl}>
                        <Box
                            sx={{
                                ...PopoverItemStyles,
                                '& > a': {
                                    color: 'currentColor',
                                    textDecoration: 'none',
                                },
                                '&:hover > a': {
                                    color: 'white',
                                    textDecoration: 'none',
                                },
                            }}
                            onClick={() => setProfileAnchorEl(null)}
                            data-testid="profile_link"
                        >
                            <Link to={`/${UI_PROFILE_PATH}/me`}>{t('profile')}</Link>
                        </Box>
                        <Box sx={PopoverItemStyles} onClick={handleLogout} data-testid="logout_btn">
                            {t('logout')}
                        </Box>
                    </CommonPopover>
                    <CommonPopover anchorEl={languageAnchorEl} setAnchorEl={setLanguageAnchorEl}>
                        <Box
                            sx={PopoverItemStyles}
                            onClick={handleLanguageChange(AppLanguage.ENG)}
                            data-testid="lang_en_btn"
                        >
                            EN
                        </Box>
                        <Box
                            sx={PopoverItemStyles}
                            onClick={handleLanguageChange(AppLanguage.RU)}
                            data-testid="lang_ru_btn"
                        >
                            RU
                        </Box>
                    </CommonPopover>
                    <CommonPopover anchorEl={themeAnchorEl} setAnchorEl={setThemeAnchorEl}>
                        <Box
                            sx={{ ...PopoverItemStyles, ...PopoverItemThemeStyles }}
                            onClick={handleThemeChange(ThemeMode.LIGHT)}
                            data-testid="theme_light_btn"
                        >
                            <LightModeOutlinedIcon sx={{ fontSize: 17 }} />
                            {t('light_theme')}
                        </Box>
                        <Box
                            sx={{ ...PopoverItemStyles, ...PopoverItemThemeStyles }}
                            onClick={handleThemeChange(ThemeMode.DARK)}
                            data-testid="theme_dark_btn"
                        >
                            <DarkModeOutlinedIcon sx={{ fontSize: 17 }} />
                            {t('dark_theme')}
                        </Box>
                        <Box
                            sx={{ ...PopoverItemStyles, ...PopoverItemThemeStyles }}
                            onClick={handleThemeChange(ThemeMode.SYSTEM)}
                            data-testid="theme_system_btn"
                        >
                            <SettingsBrightnessIcon sx={{ fontSize: 17 }} />
                            {t('system_theme')}
                        </Box>
                    </CommonPopover>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton onClick={(e) => setThemeAnchorEl(e.currentTarget)} data-testid="theme_btn">
                            {getThemeIcon()}
                        </IconButton>
                        <IconButton onClick={(e) => setLanguageAnchorEl(e.currentTarget)} data-testid="language_btn">
                            <LanguageIcon />
                        </IconButton>
                        <IconButton onClick={(e) => setQuestionAnchorEl(e.currentTarget)} data-testid="help_btn">
                            <QuestionMarkIcon />
                        </IconButton>
                        <IconButton onClick={(e) => setProfileAnchorEl(e.currentTarget)} data-testid="profile_btn">
                            <PersonOutlinedIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default Topbar;
